import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { BotanicalCorner, GoldLineDivider, FlowingWaves } from '@/components/feature/DrawingLines';

// ── Data ──────────────────────────────────────────────────────────────────────

const heroSlides = [
  {
    src: 'https://storage.readdy-site.link/project_files/6c70749e-1a13-4501-8c52-18d81429d110/114de32f-b614-46c0-8002-d0515a4bb244_unnamed.png?v=ac79ab60fc4a43fe15da075f6383523f',
    caption: '갤러리 무드의 메인 라운지',
    objectPosition: 'center 15%',
  },
  {
    src: 'https://storage.readdy-site.link/project_files/6c70749e-1a13-4501-8c52-18d81429d110/c0dfce98-4d8d-41b5-a838-fe6973391e5b_unnamed.png?v=b48c541f3bad5df4c3845851e81fa23f',
    caption: '프라이빗 1:1 상담실',
    objectPosition: 'center 20%',
  },
  {
    src: 'https://storage.readdy-site.link/project_files/6c70749e-1a13-4501-8c52-18d81429d110/727d5fa6-8bb4-4b10-8749-05a141ad12f1_unnamed.png?v=1fd91a57d4d3f0f22eca537b24e48dc2',
    caption: '위생 존 (Hygiene Zone)',
    objectPosition: 'center 25%',
  },
  {
    src: 'https://storage.readdy-site.link/project_files/6c70749e-1a13-4501-8c52-18d81429d110/4b483db6-944a-42e8-8713-f342d346eb78_unnamed.png?v=1c9c30a25d2baee2968ce31292004241',
    caption: '아티스트 전용 작업실',
    objectPosition: 'center 20%',
  },
  {
    src: 'https://storage.readdy-site.link/project_files/6c70749e-1a13-4501-8c52-18d81429d110/bb4f7514-c370-4219-b587-873067ebd159_unnamed.png?v=24f73f840ad4924b87e866eff1e2ee84',
    caption: '파우더룸',
    objectPosition: 'center 20%',
  },
];

const spaces = [
  {
    id: 'lounge',
    title: '갤러리 라운지',
    subtitle: 'Gallery Lounge',
    desc: '고객이 처음 마주하는 공간. 현대 미술관의 감성을 담아 타투에 대한 편견을 지우고 프리미엄 경험의 시작을 알립니다.',
    tags: ['#갤러리무드', '#오픈형스튜디오'],
    src: 'https://storage.readdy-site.link/project_files/6c70749e-1a13-4501-8c52-18d81429d110/114de32f-b614-46c0-8002-d0515a4bb244_unnamed.png?v=ac79ab60fc4a43fe15da075f6383523f',
    icon: 'ri-gallery-line',
    tall: true,
  },
  {
    id: 'consultation',
    title: '프라이빗 상담실',
    subtitle: 'Private Consultation',
    desc: '고객과 아티스트가 깊은 대화를 나누는 공간. 완전한 프라이버시와 편안한 분위기로 최적의 디자인 협의를 이끌어냅니다.',
    tags: ['#프라이빗상담실', '#갤러리무드'],
    src: 'https://storage.readdy-site.link/project_files/6c70749e-1a13-4501-8c52-18d81429d110/c0dfce98-4d8d-41b5-a838-fe6973391e5b_unnamed.png?v=b48c541f3bad5df4c3845851e81fa23f',
    icon: 'ri-discuss-line',
    tall: false,
  },
  {
    id: 'hygiene',
    title: '위생 존',
    subtitle: 'Hygiene Zone',
    desc: '인테리어가 예쁘기만 한 게 아닙니다. 별도 멸균실과 체계적인 위생 동선으로 시스템적 완벽함을 구현합니다.',
    tags: ['#메디컬위생기준', '#프라이빗상담실'],
    src: 'https://storage.readdy-site.link/project_files/6c70749e-1a13-4501-8c52-18d81429d110/727d5fa6-8bb4-4b10-8749-05a141ad12f1_unnamed.png?v=1fd91a57d4d3f0f22eca537b24e48dc2',
    icon: 'ri-shield-check-line',
    tall: false,
  },
  {
    id: 'studio',
    title: '아티스트 작업실',
    subtitle: 'Artist Studio',
    desc: '최고의 집중력을 위해 설계된 작업 공간. 고광량 무영 조명과 인체공학적 배치로 아티스트의 퍼포먼스를 극대화합니다.',
    tags: ['#메디컬위생기준', '#오픈형스튜디오'],
    src: 'https://storage.readdy-site.link/project_files/6c70749e-1a13-4501-8c52-18d81429d110/4b483db6-944a-42e8-8713-f342d346eb78_unnamed.png?v=1c9c30a25d2baee2968ce31292004241',
    icon: 'ri-pencil-ruler-2-line',
    tall: false,
  },
  {
    id: 'powder',
    title: '파우더룸',
    subtitle: 'Powder Room',
    desc: '시술 후 고객이 편안하게 마무리할 수 있는 프라이빗 공간. 세심한 배려와 고급스러운 마감으로 완벽한 경험을 완성합니다.',
    tags: ['#프라이빗공간', '#고객배려'],
    src: 'https://storage.readdy-site.link/project_files/6c70749e-1a13-4501-8c52-18d81429d110/bb4f7514-c370-4219-b587-873067ebd159_unnamed.png?v=24f73f840ad4924b87e866eff1e2ee84',
    icon: 'ri-user-heart-line',
    tall: false,
  },
];

const specs = [
  { icon: 'ri-contrast-2-line', title: '화이트 도장 마감', desc: '반사율 최적화 무광 화이트 도장으로 갤러리 감성과 청결함을 동시에 구현' },
  { icon: 'ri-volume-mute-line', title: '무소음 바닥재', desc: '방음 기능이 탑재된 프리미엄 LVT 바닥재로 작업 집중도 향상 및 소음 차단' },
  { icon: 'ri-shield-flash-line', title: '전용 살균 시스템', desc: 'UV-C 공기 살균기 + 오존 발생기 조합으로 24시간 공기 중 세균 99.9% 제거' },
  { icon: 'ri-lightbulb-flash-line', title: '고광량 무영 조명', desc: '수술실 기준 조도 1,000lux 이상의 무영 조명으로 정밀 작업 환경 구현' },
  { icon: 'ri-temp-cold-line', title: '독립 냉난방 시스템', desc: '공간별 개별 온도 제어로 고객 쾌적성과 잉크 보관 최적 온도 동시 유지' },
  { icon: 'ri-door-lock-line', title: '프라이버시 파티션', desc: '반투명 강화유리 파티션으로 개방감과 프라이버시를 동시에 확보' },
  { icon: 'ri-water-flash-line', title: '의료용 세면 시스템', desc: '발 조작식 수전 + 의료용 항균 비누 디스펜서로 교차 오염 원천 차단' },
  { icon: 'ri-archive-drawer-line', title: '내장형 수납 시스템', desc: '소모품 전용 내장 수납장으로 깔끔한 동선과 위생적인 보관 환경 유지' },
];

const floorPlanFeatures = [
  { icon: 'ri-map-2-line', label: '라운지 & 리셉션', area: '약 3평' },
  { icon: 'ri-discuss-line', label: '상담실', area: '약 2평' },
  { icon: 'ri-pencil-ruler-2-line', label: '작업실 (1~2 베이)', area: '약 4평' },
  { icon: 'ri-shield-check-line', label: '위생 존 & 멸균실', area: '약 1평' },
  { icon: 'ri-user-heart-line', label: '파우더룸', area: '약 1평' },
];

const floorPlanSrc = 'https://storage.readdy-site.link/project_files/6c70749e-1a13-4501-8c52-18d81429d110/c95ddb2e-bada-4bd9-a4db-30159b03c7ac_unnamed.png?v=918066bbf578e5cae64ff075d6686e46';

const beforeSrc = 'https://public.readdy.ai/ai/img_res/3fb0984202e774ba94e1f11d3114ee33.jpg';

const afterSrc = 'https://storage.readdy-site.link/project_files/6c70749e-1a13-4501-8c52-18d81429d110/d3967e99-b2b4-4ac2-9bde-c0a7ca91a536_Gemini_Generated_Image_cue4vacue4vacue4.png?v=362b1bb8e5e4e5f6c8e3fadffbec5a79';

// ── Hero Slider ───────────────────────────────────────────────────────────────

function HeroSlider() {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const goTo = useCallback((idx: number) => {
    if (animating) return;
    setAnimating(true);
    setTimeout(() => {
      setCurrent(idx);
      setAnimating(false);
    }, 400);
  }, [animating]);

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      goTo((current + 1) % heroSlides.length);
    }, 4500);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [current, goTo]);

  const slide = heroSlides[current];

  return (
    <section className="relative overflow-hidden bg-[#1A1A1A]" style={{ height: 'min(90vw, 85vh)' }}>
      <div
        className="absolute inset-0 transition-opacity duration-700"
        style={{ opacity: animating ? 0 : 1 }}
      >
        {/* 블러 배경 */}
        <img
          src={slide.src}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover scale-110"
          style={{ filter: 'blur(24px) brightness(0.45)', transform: 'scale(1.15)' }}
        />
        {/* 메인 이미지 */}
        <img
          src={slide.src}
          alt={slide.caption}
          className="relative w-full h-full object-contain z-10"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/80 via-[#1A1A1A]/20 to-[#1A1A1A]/30" />
      </div>

      <div className="relative z-10 h-full flex flex-col justify-end pb-16 md:pb-24 px-8 md:px-16 max-w-[1440px] mx-auto w-full">
        <div
          className="transition-all duration-700"
          style={{ opacity: animating ? 0 : 1, transform: animating ? 'translateY(10px)' : 'translateY(0)' }}
        >
          <p className="text-[10px] tracking-[0.4em] uppercase text-[#C9A96E] mb-3">
            {String(current + 1).padStart(2, '0')} / {String(heroSlides.length).padStart(2, '0')}
          </p>
          <h2
            className="text-2xl md:text-4xl text-white mb-2 leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}
          >
            {slide.caption}
          </h2>
        </div>

        <div className="flex items-center gap-2 mt-6">
          {heroSlides.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className="cursor-pointer transition-all duration-300"
            >
              <span
                className="block rounded-full transition-all duration-300"
                style={{
                  width: i === current ? '24px' : '6px',
                  height: '6px',
                  background: i === current ? '#C9A96E' : 'rgba(255,255,255,0.3)',
                }}
              />
            </button>
          ))}
        </div>
      </div>

      <button
        onClick={() => goTo((current - 1 + heroSlides.length) % heroSlides.length)}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center border border-white/30 text-white hover:bg-white/10 transition-all duration-200 cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-xl" />
      </button>
      <button
        onClick={() => goTo((current + 1) % heroSlides.length)}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center border border-white/30 text-white hover:bg-white/10 transition-all duration-200 cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-xl" />
      </button>
    </section>
  );
}

// ── Page Hero Header ──────────────────────────────────────────────────────────

function InteriorHero() {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), 100); return () => clearTimeout(t); }, []);

  return (
    <section className="relative flex items-center overflow-hidden bg-[#1A1A1A]" style={{ minHeight: '620px' }}>
      {/* 배경 이미지 */}
      <img
        src="https://storage.readdy-site.link/project_files/6c70749e-1a13-4501-8c52-18d81429d110/114de32f-b614-46c0-8002-d0515a4bb244_unnamed.png?v=ac79ab60fc4a43fe15da075f6383523f"
        alt=""
        className="absolute inset-0 w-full h-full object-cover object-center opacity-50"
      />
      {/* 오버레이 */}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(10,10,10,0.55) 0%, rgba(10,10,10,0.35) 40%, rgba(10,10,10,0.8) 100%)' }} />
      {/* 골드 상단 라인 */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-[#C9A96E]" />
      <FlowingWaves color="rgba(201,169,110,0.08)" lineCount={4} />

      <div
        className="relative z-10 w-full max-w-[1440px] mx-auto px-6 md:px-16 pt-36 pb-20 md:pt-40 md:pb-24 text-center"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(24px)',
          transition: 'opacity 0.9s ease-out, transform 0.9s ease-out',
        }}
      >
        {/* Breadcrumb */}
        <div className="flex items-center justify-center gap-2 mb-10">
          <Link to="/franchise" className="text-[10px] tracking-[0.3em] uppercase text-white/40" style={{ textDecoration: 'none' }}>
            Franchise
          </Link>
          <span className="text-white/20">/</span>
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#C9A96E]">인테리어 컨셉</span>
        </div>

        {/* 타이틀 */}
        <h1
          className="text-[32px] md:text-[54px] text-white mb-6 leading-snug break-keep"
          style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.06em' }}
        >
          예술이 머무는<br />
          <span style={{ color: 'rgba(255,255,255,0.35)' }}>가장 깨끗한 공간</span>
        </h1>

        {/* 설명 텍스트 */}
        <p
          className="text-sm md:text-[15px] leading-relaxed break-keep max-w-[320px] md:max-w-none mx-auto"
          style={{ color: 'rgba(255,255,255,0.55)' }}
        >
          타투의 어두운 편견을 지우고, 현대 미술관의 감성과{' '}
          메디컬 등급의 위생을 결합했습니다.{' '}
          아티스트에게는 최고의 몰입을, 고객에게는 최상의 안락함을.
        </p>

        {/* 구분선 */}
        <div className="w-14 h-px mx-auto my-10" style={{ background: 'rgba(201,169,110,0.5)' }} />

        {/* 통계 */}
        <div className="flex flex-wrap items-center justify-center gap-y-8 gap-x-10 md:gap-x-16">
          {[
            { value: '5가지', label: '핵심 공간 구성' },
            { value: '8가지', label: '표준 인테리어 사양' },
            { value: '100%', label: '본사 가이드 시공' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div
                className="text-3xl md:text-[32px] text-white font-bold mb-2"
                style={{ fontFamily: "'Montserrat', sans-serif", letterSpacing: '-0.01em', lineHeight: 1.1 }}
              >
                {stat.value}
              </div>
              <div className="text-[10px] tracking-[0.25em] uppercase" style={{ color: 'rgba(255,255,255,0.35)' }}>
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Space Grid ────────────────────────────────────────────────────────────────

function SpaceGrid() {
  const { ref, visible } = useScrollAnimation(0.05);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  return (
    <section className="py-28 md:py-36 bg-[#FAFAFA] overflow-hidden relative" ref={ref}>
      <BotanicalCorner position="top-left" color="rgba(201,169,110,0.08)" size={150} />
      <div className="max-w-[1440px] mx-auto px-8 md:px-16">
        <div
          className="text-center mb-10 transition-all duration-1000 ease-out"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)' }}
        >
          <p className="text-xs tracking-[0.35em] uppercase text-[#C9A96E] mb-5" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}>Space Concept</p>
          <h2
            className="text-3xl md:text-4xl text-[#222222] mb-5"
            style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.08em' }}
          >
            선율 갤러리의 5가지 핵심 공간
          </h2>
          <p className="text-sm text-[#888] max-w-md mx-auto leading-relaxed" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", lineHeight: '1.75' }}>
            각 공간은 고객 경험의 흐름에 맞게 설계되었습니다.<br />
            입장부터 시술, 사후 관리까지 완벽한 동선을 구현합니다.
          </p>
        </div>
        <div className="mb-12">
          <GoldLineDivider delay={0.2} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {spaces.map((space, i) => (
            <div
              key={space.id}
              className="group bg-white border border-[#EBEBEB] p-8 hover:border-[#C9A96E] hover:shadow-lg transition-all duration-300 cursor-default"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(40px)',
                transition: 'opacity 0.7s ease-out, transform 0.7s ease-out, border-color 0.3s, box-shadow 0.3s',
                transitionDelay: `${i * 80 + 200}ms`,
              }}
              onMouseEnter={() => setHoveredId(space.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              {/* 번호 */}
              <div className="flex items-center justify-between mb-6">
                <span
                  className="text-4xl font-light text-[#EBEBEB] group-hover:text-[#C9A96E]/30 transition-colors duration-300"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div className="w-12 h-12 flex items-center justify-center border border-[#EBEBEB] group-hover:border-[#C9A96E] group-hover:bg-[#C9A96E] transition-all duration-300">
                  <i className={`${space.icon} text-xl text-[#AAAAAA] group-hover:text-white transition-colors duration-300`} />
                </div>
              </div>

              {/* 타이틀 */}
              <p className="text-[10px] tracking-[0.25em] uppercase text-[#C9A96E] mb-2">{space.subtitle}</p>
              <h3
                className="text-xl text-[#1A1A1A] mb-4 leading-tight"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
              >
                {space.title}
              </h3>

              {/* 설명 */}
              <p className="text-xs text-[#888] leading-relaxed mb-5 min-h-[60px]">
                {space.desc}
              </p>

              {/* 태그 */}
              <div className="flex flex-wrap gap-2">
                {space.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-[10px] px-3 py-1.5 bg-[#F5F5F5] text-[#888] group-hover:bg-[#C9A96E]/10 group-hover:text-[#C9A96E] transition-colors duration-300 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Floor Plan ────────────────────────────────────────────────────────────────

function FloorPlanSection() {
  const { ref, visible } = useScrollAnimation(0.1);

  return (
    <section className="py-28 md:py-36 bg-white overflow-hidden" ref={ref}>
      <div className="max-w-[1440px] mx-auto px-8 md:px-16">
        <div
          className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center transition-all duration-1000 ease-out"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(40px)' }}
        >
          <div>
            <p className="text-xs tracking-[0.35em] uppercase text-[#C9A96E] mb-5">Space Planning</p>
            <h2
              className="text-3xl md:text-4xl text-[#1A1A1A] mb-6 leading-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
            >
              본사가 직접 설계하는<br />
              <span className="text-[#AAAAAA]">최적화된 공간 구성</span>
            </h2>
            <p className="text-sm text-[#888] leading-relaxed mb-10">
              10평 기준 표준 평면도를 바탕으로, 상가 구조에 맞게 최적화된 공간 배치를 제안합니다.
              본사 인테리어팀이 도면 설계부터 시공 감리까지 전 과정을 지원합니다.
            </p>

            <div className="grid grid-cols-2 gap-4">
              {floorPlanFeatures.map((f, i) => (
                <div
                  key={f.label}
                  className="flex items-start gap-3 p-4 border border-[#EBEBEB] hover:border-[#C9A96E] transition-colors duration-300 cursor-default"
                  style={{
                    opacity: visible ? 1 : 0,
                    transform: visible ? 'translateY(0)' : 'translateY(20px)',
                    transition: 'opacity 0.6s ease-out, transform 0.6s ease-out, border-color 0.3s',
                    transitionDelay: `${i * 80 + 400}ms`,
                  }}
                >
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className={`${f.icon} text-[#C9A96E] text-lg`} />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-[#1A1A1A] mb-0.5">{f.label}</p>
                    <p className="text-[10px] text-[#AAAAAA]">{f.area}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div
            className="relative transition-all duration-1000 ease-out"
            style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateX(0)' : 'translateX(40px)', transitionDelay: '200ms' }}
          >
            <div className="relative border border-[#EBEBEB] bg-[#F8F7F5]">
              <img
                src={floorPlanSrc}
                alt="선율 표준 평면도"
                className="w-full object-contain"
                style={{ display: 'block', maxHeight: '480px' }}
              />
              <div className="absolute top-4 left-4">
                <span className="text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 bg-[#1A1A1A] text-white font-medium">
                  10평 표준 평면도
                </span>
              </div>
            </div>
            <p className="text-[11px] text-[#AAAAAA] mt-3 leading-relaxed">
              ※ 실제 상가 구조에 따라 배치가 조정될 수 있습니다. 본사 인테리어팀이 맞춤 설계를 지원합니다.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Spec List ─────────────────────────────────────────────────────────────────

function SpecSection() {
  const { ref, visible } = useScrollAnimation(0.05);

  return (
    <section className="py-28 md:py-36 bg-[#FAFAFA] overflow-hidden relative" ref={ref}>
      <BotanicalCorner position="bottom-right" color="rgba(201,169,110,0.08)" size={150} />
      <div className="max-w-[1440px] mx-auto px-8 md:px-16">

        <div
          className="text-center mb-10 transition-all duration-1000 ease-out"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)' }}
        >
          <p className="text-xs tracking-[0.35em] uppercase text-[#C9A96E] mb-5" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}>Standard Specifications</p>
          <h2
            className="text-3xl md:text-4xl text-[#222222] mb-5"
            style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.08em' }}
          >
            선율 표준 인테리어 사양
          </h2>
          <p className="text-sm text-[#888] max-w-md mx-auto leading-relaxed" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", lineHeight: '1.75' }}>
            모든 선율 가맹점은 동일한 표준 사양을 적용합니다.<br />
            어느 지점을 방문해도 동일한 프리미엄 경험을 보장합니다.
          </p>
        </div>
        <div className="mb-12">
          <GoldLineDivider delay={0.2} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {specs.map((spec, i) => (
            <div
              key={spec.title}
              className="group bg-white border border-[#EBEBEB] p-7 hover:border-[#C9A96E] transition-all duration-300 cursor-default"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(40px)',
                transition: 'opacity 0.7s ease-out, transform 0.7s ease-out, border-color 0.3s',
                transitionDelay: `${i * 80 + 200}ms`,
              }}
            >
              <div className="w-10 h-10 flex items-center justify-center mb-5 border border-[#EBEBEB] group-hover:border-[#C9A96E] group-hover:bg-[#C9A96E] transition-all duration-300">
                <i className={`${spec.icon} text-lg text-[#AAAAAA] group-hover:text-white transition-colors duration-300`} />
              </div>
              <h3 className="text-sm font-semibold text-[#1A1A1A] mb-2.5 leading-snug">{spec.title}</h3>
              <p className="text-xs text-[#888] leading-relaxed">{spec.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Before & After ────────────────────────────────────────────────────────────

function BeforeAfterSection() {
  const { ref, visible } = useScrollAnimation(0.1);
  const [sliderX, setSliderX] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const updateSlider = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const pct = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
    setSliderX(pct);
  }, []);

  const onMouseDown = () => { dragging.current = true; };
  const onMouseMove = (e: React.MouseEvent) => { if (dragging.current) updateSlider(e.clientX); };
  const onMouseUp = () => { dragging.current = false; };
  const onTouchMove = (e: React.TouchEvent) => { updateSlider(e.touches[0].clientX); };

  return (
    <section className="py-28 md:py-36 bg-white overflow-hidden" ref={ref}>
      <div className="max-w-[1440px] mx-auto px-8 md:px-16">

        <div
          className="text-center mb-20 transition-all duration-1000 ease-out"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)' }}
        >
          <p className="text-xs tracking-[0.35em] uppercase text-[#C9A96E] mb-5" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}>Before &amp; After</p>
          <h2
            className="text-3xl md:text-4xl text-[#222222] mb-5"
            style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.08em' }}
          >
            일반 상가에서<br />
            <span className="text-[#AAAAAA]">선율 갤러리로</span>
          </h2>
          <p className="text-sm text-[#888] max-w-md mx-auto leading-relaxed" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", lineHeight: '1.75' }}>
            슬라이더를 드래그하여 시공 전후를 직접 비교해보세요.<br />
            본사의 시공 능력이 공간을 어떻게 변화시키는지 확인하세요.
          </p>
        </div>

        <div
          className="transition-all duration-1000 ease-out"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(40px)', transitionDelay: '300ms' }}
        >
          <div
            ref={containerRef}
            className="relative overflow-hidden select-none cursor-col-resize"
            style={{ height: '500px' }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
            onTouchMove={onTouchMove}
          >
            {/* After — full base */}
            <img
              src={afterSrc}
              alt="선율 갤러리 시공 후"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: 'center 20%' }}
            />

            {/* Before — clipped left portion */}
            <div
              className="absolute inset-0 overflow-hidden"
              style={{ width: `${sliderX}%` }}
            >
              <img
                src={beforeSrc}
                alt="시공 전 일반 상가"
                className="absolute inset-0 h-full object-cover object-top"
                style={{ width: `${10000 / Math.max(sliderX, 1)}%`, maxWidth: 'none' }}
              />
            </div>

            {/* Divider */}
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-white z-20 pointer-events-none"
              style={{ left: `${sliderX}%` }}
            >
              <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-white flex items-center justify-center">
                <i className="ri-arrow-left-right-line text-[#1A1A1A] text-sm" />
              </div>
            </div>

            <div className="absolute top-5 left-5 z-10">
              <span className="text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 bg-[#555]/80 text-white font-medium backdrop-blur-sm">
                BEFORE
              </span>
            </div>
            <div className="absolute top-5 right-5 z-10">
              <span className="text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 bg-[#C9A96E]/90 text-white font-medium backdrop-blur-sm">
                AFTER
              </span>
            </div>
          </div>

          <p className="text-[11px] text-[#AAAAAA] mt-4 text-center">
            ※ 슬라이더를 좌우로 드래그하여 시공 전후를 비교하세요
          </p>
        </div>
      </div>
    </section>
  );
}

// ── Signage Design ────────────────────────────────────────────────────────────

function SignageSection() {
  const { ref, visible } = useScrollAnimation(0.1);

  return (
    <section className="py-28 md:py-36 bg-[#1A1A1A] overflow-hidden relative" ref={ref}>
      <FlowingWaves color="rgba(201,169,110,0.06)" lineCount={4} />
      <BotanicalCorner position="top-right" color="rgba(201,169,110,0.12)" size={160} />
      <BotanicalCorner position="bottom-left" color="rgba(201,169,110,0.10)" size={140} />

      <div className="relative z-10 max-w-[1440px] mx-auto px-8 md:px-16">
        {/* Header */}
        <div
          className="text-center mb-16 transition-all duration-1000 ease-out"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)' }}
        >
          <p
            className="text-[10px] tracking-[0.5em] uppercase text-[#C9A96E] mb-5"
            style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}
          >
            Signage Design
          </p>
          <h2
            className="text-3xl md:text-4xl text-white mb-5 leading-tight"
            style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.08em' }}
          >
            브랜드를 완성하는<br />
            <span className="text-white/35">간판 디자인</span>
          </h2>
          <p
            className="text-sm text-white/40 max-w-md mx-auto leading-relaxed"
            style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", lineHeight: '1.75' }}
          >
            선율의 아이덴티티를 외부에서도 동일하게 전달합니다.<br />
            절제된 골드 레터링과 미니멀한 구성으로 공간의 품격을 높입니다.
          </p>
        </div>

        {/* Main Image */}
        <div
          className="transition-all duration-1000 ease-out"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(40px)', transitionDelay: '200ms' }}
        >
          <div className="relative border border-white/10 overflow-hidden">
            <img
              src="https://storage.readdy-site.link/project_files/6c70749e-1a13-4501-8c52-18d81429d110/53e5d062-a818-4a38-9a2f-1f53f7fc8619_Gemini_Generated_Image_4jp0ph4jp0ph4jp0.png?v=13417d41fffa43ce3918876da790c8b0"
              alt="선율 라인 스튜디오 간판 디자인"
              className="w-full h-auto block"
            />
            {/* 배지 */}
            <div className="absolute top-6 left-6">
              <span
                className="text-[10px] tracking-[0.25em] uppercase px-4 py-2 bg-[#C9A96E] text-white font-medium"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                Standard Signage
              </span>
            </div>
          </div>
        </div>

        {/* Feature Points */}
        <div
          className="grid grid-cols-1 sm:grid-cols-3 gap-0 mt-0 border-t border-white/10"
          style={{
            opacity: visible ? 1 : 0,
            transition: 'opacity 0.9s ease-out',
            transitionDelay: '400ms',
          }}
        >
          {[
            {
              icon: 'ri-contrast-2-line',
              title: '골드 레터링',
              desc: '브러시드 골드 채널 레터로 야간에도 선명한 브랜드 인지도를 확보합니다.',
            },
            {
              icon: 'ri-layout-line',
              title: '미니멀 구성',
              desc: '불필요한 장식을 배제하고 로고와 브랜드명만으로 완성되는 절제의 미학.',
            },
            {
              icon: 'ri-building-2-line',
              title: '본사 표준 시공',
              desc: '전국 모든 가맹점에 동일한 간판 규격을 적용하여 브랜드 일관성을 유지합니다.',
            },
          ].map((item, i) => (
            <div
              key={item.title}
              className="flex flex-col sm:flex-row items-start gap-5 px-8 py-8 border-b sm:border-b-0 sm:border-r border-white/10 last:border-0 group"
              style={{
                opacity: visible ? 1 : 0,
                transition: 'opacity 0.7s ease-out',
                transitionDelay: `${i * 100 + 500}ms`,
              }}
            >
              <div className="w-10 h-10 flex items-center justify-center border border-white/15 group-hover:border-[#C9A96E] group-hover:bg-[#C9A96E] transition-all duration-300 flex-shrink-0">
                <i className={`${item.icon} text-base text-white/40 group-hover:text-white transition-colors duration-300`} />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-white/80 mb-2">{item.title}</h4>
                <p className="text-xs text-white/35 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── CTA ───────────────────────────────────────────────────────────────────────

function InteriorCta() {
  const { ref, visible } = useScrollAnimation(0.2);

  return (
    <section className="py-28 bg-[#1A1A1A] overflow-hidden relative" ref={ref}>
      <FlowingWaves color="rgba(201,169,110,0.08)" lineCount={3} />
      <BotanicalCorner position="top-right" color="rgba(201,169,110,0.18)" size={150} />
      <BotanicalCorner position="bottom-left" color="rgba(201,169,110,0.15)" size={130} />
      <div className="relative z-10 max-w-[1440px] mx-auto px-8 md:px-16 text-center">
        <div
          className="transition-all duration-1000 ease-out"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)' }}
        >
          <p className="text-xs tracking-[0.35em] uppercase text-[#C9A96E] mb-6" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}>Start Your Space</p>
          <h2
            className="text-3xl md:text-5xl text-white mb-6 leading-tight"
            style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.08em' }}
          >
            당신의 공간을<br />
            <span className="text-white/40">선율 갤러리로 만들어드립니다.</span>
          </h2>
          <p className="text-sm text-white/40 leading-relaxed max-w-md mx-auto mb-12" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", lineHeight: '1.75' }}>
            본사 인테리어팀이 도면 설계부터 시공 감리까지<br />
            모든 과정을 함께합니다.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/franchise/guide"
              className="px-10 py-4 bg-[#C9A96E] text-white text-[10px] tracking-[0.2em] uppercase font-medium rounded-full hover:bg-[#b8955a] transition-colors duration-300 cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              창업 비용 확인하기
            </Link>
            <Link
              to="/community/inquiry"
              className="px-10 py-4 border border-white/20 text-white/60 text-[10px] tracking-[0.2em] uppercase font-medium rounded-full hover:border-white/50 hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              인테리어 상담 문의
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function FranchiseInteriorPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        <InteriorHero />
        <HeroSlider />
        <SpaceGrid />
        <FloorPlanSection />
        <SignageSection />
        <SpecSection />
        <BeforeAfterSection />
        <InteriorCta />
      </main>
      <Footer />
    </div>
  );
}
